import { GeoJson } from '@ais-ui/api-spec/shapes';
import React from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useShapeExport } from '@ais-ui/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useToast,
} from '@ais-ui/ui-core';

interface ExportShapeDropdownMenuProps {
  shape: GeoJson;
}

export const ExportShapeDropdownMenu = ({
  shape,
}: ExportShapeDropdownMenuProps) => {
  const { messages } = useLocalization();
  const { exportShape } = useShapeExport();
  const { toast } = useToast();

  const shapeId = shape?.properties?.id ? shape.properties.id : 'tempShapeId';

  const onExport = async (format: string) => {
    try {
      const data = await exportShape(shapeId, format);

      let blob;
      let mimeType;
      let fileExtension;

      switch (format) {
        case 'geojson':
          blob = new Blob([JSON.stringify(data, null, 2)], {
            type: 'application/json',
          });
          mimeType = 'application/json';
          fileExtension = 'geojson';
          break;
        case 'kml':
          blob = new Blob([data], {
            type: 'application/vnd.google-earth.kml+xml',
          });
          mimeType = 'application/vnd.google-earth.kml+xml';
          fileExtension = 'kml';
          break;
        case 'wkt':
          blob = new Blob([data], { type: 'text/plain' });
          mimeType = 'text/plain';
          fileExtension = 'wkt';
          break;
        default:
          throw new Error('Unsupported format');
      }

      // Create a link element
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = `${shape.properties.name ?? shape.properties.id ?? 'shape'}.${fileExtension}`;

      // Append the link to the document body and click it to trigger the download
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      // NBS: Ideally we want to close the dropdown menu after the export is done, but this freezes the application
      //  so if you want to close it make sure, that this issue is fixed first.
    } catch (error) {
      console.error('Error during export:', error);
      toast({
        title: messages.genericToastTitleError,
        description: `Shape export for ${format} failed`,
        variant: 'destructive',
      });
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <DropdownMenuItem>{messages.shapeDropDownExport}</DropdownMenuItem>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="left">
        <DropdownMenuItem
          onClick={() => {
            onExport('geojson');
          }}
        >
          {messages.shapeDropDownGeojson}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            onExport('kml');
          }}
        >
          {messages.shapeDropDownKml}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            onExport('wkt');
          }}
        >
          {messages.shapeDropDownWkt}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
